import { formatAsCurrency } from "@/data/amounts";
import { classNames } from "@/data/classnames";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Fragment, ReactNode } from "react";
import {
  DraftInvoice,
  Invoice,
  Lot,
  PaymentMethod,
  PayoutMethod,
  SimplePaymentIn,
  SimplePaymentOut,
} from "types";
import { Spinner } from "../Buttons";
import { useCheckoutSheetContext } from "./CheckoutSheetProvider";
import { PotentialPayoutsIcon } from "./PotentialPayoutsIcon";

const showCurrency = false;
const removeTrailingZero = false;

// Available payout methods
const PAYOUT_METHODS: PayoutMethod[] = ["BACS", "Cheque", "Cash"];

// Define a simple type for potential payouts based on the user example
type PotentialPayout = {
  id: string;
  amountInCents: number;
  paymentMethod: string;
  invoice?: {
    id: string;
    invoiceNumber: number;
    fullyPaid: boolean;
  };
};

export function getInvoiceSummaryPanelData(
  lots: Lot[] | null | undefined,
  invoice: DraftInvoice | Invoice | null | undefined,
  isLoading: boolean
) {
  let panelData: PanelProps = {
    title: "Summary",
    aggregates: [],
    subtotals: [],
    total: "-",
    isLoading,
  };

  if (!lots && !invoice) {
  } else if (lots && !invoice) {
    // let lotsTotal = lots.reduce(
    //   (acc, lot) => acc + (lot.generated?.totalValueInCents || 0),
    //   0
    // );
    panelData = {
      title: "Summary",
      aggregates: [
        // {
        //   label: "Lots",
        //   value: formatAsCurrency(null, lotsTotal),
        // },
      ],
      subtotals: [
        {
          label: "Subtotal",
          value: "",
        },
        {
          label: "Tax on Lots",
          value: "",
        },
        {
          label: "Tax on Commission",
          value: "",
        },

        {
          label: "Tax on Additions",
          value: "",
        },
      ],
      total: "-",
      isLoading,
    };
  } else if (invoice) {
    let i = invoice;

    let currency = showCurrency ? i.currency : null;

    let isSeller = i.clientType === "Seller";
    let isBuyer = i.clientType === "Buyer";
    let isVATReg = i.customerIsVATRegistered;
    let isWithinTaxRegion = i.customerWithinTaxRegion;

    // Seller - return vat if vat registered
    // keep vat if not vat registered. Should be caught by lot gremlin
    let servicesVatMultiplier = 0;
    let goodsVatMultiplier = 0;
    if (isSeller) {
      servicesVatMultiplier = -1;
      goodsVatMultiplier = isVATReg ? 1 : -1;
    }

    // Buyer 0 vat if vta reg and not in tax region
    if (isBuyer) {
      servicesVatMultiplier = 1;
      goodsVatMultiplier = isVATReg && !isWithinTaxRegion ? 0 : 1;
    }

    let subtotal =
      i.lotTotalInCentsExVat +
      ((i.commissionTotalInCents || 0) +
        (i.nonCommissionAdjustmentsInCents || 0)) *
        servicesVatMultiplier;

    panelData = {
      title: "Summary",
      aggregates: [
        // {
        //   label: "Lots",
        //   value: formatAsCurrency(
        //     currency,
        //     i.lotTotalInCentsExVat,
        //     removeTrailingZero
        //   ),
        // },
        // This should be in the invoice subtitalGroupTotals...
        // {
        //   label: "Extras",
        //   value: formatAsCurrency(
        //     currency,
        //     (i.nonCommissionAdjustmentsInCents || 0) * multiplier,
        //     removeTrailingZero
        //   ),
        // },
        ...Object.keys(i.subtotalGroupTotals).map((key, idx) => ({
          label: key,
          value: formatAsCurrency(
            currency,
            i.subtotalGroupTotals[key],
            removeTrailingZero
          ),
        })),
      ],
      subtotals: [
        {
          label: "Subtotal",
          value: formatAsCurrency(currency, subtotal, removeTrailingZero),
        },
        {
          label: "Tax on Lots",
          value: formatAsCurrency(
            currency,
            i.vatOnLotTotalInCents * goodsVatMultiplier,
            removeTrailingZero
          ),
        },
        {
          label: "Tax on Commission",
          value: formatAsCurrency(
            currency,
            (i.vatOnCommissionInCents || 0) * servicesVatMultiplier,
            removeTrailingZero
          ),
        },

        {
          label: "Tax on Extras",
          value: formatAsCurrency(
            currency,
            (i.vatOnAdjustmentsInCents || 0) * servicesVatMultiplier,
            removeTrailingZero
          ),
        },
      ],
      total:
        formatAsCurrency(i.currency, i.finalTotalInCents, removeTrailingZero) ||
        "Unknown",
      isLoading: false,
    };

    // If we have payment information, include it in the panel data
    if (i.amountPaidInCents != null) {
      let formattedAmountPaid = formatAsCurrency(
        i.currency,
        i.amountPaidInCents,
        removeTrailingZero
      );

      let formattedAmountDue = formatAsCurrency(
        i.currency,
        i.amountDueInCents || 0,
        removeTrailingZero
      );

      if (formattedAmountPaid) {
        panelData.amountPaid = formattedAmountPaid as string;
      }

      if (formattedAmountDue) {
        panelData.amountDue = formattedAmountDue as string;
      }
    }
  }

  return panelData;
}

// Higher level component here that converts the data into a format that we need
export function SummaryPanel(props: {
  lots?: Lot[] | null;
  invoice?: DraftInvoice | Invoice | null;
  isLoading: boolean;
}) {
  let { lots, invoice, isLoading } = props;

  let panelData = getInvoiceSummaryPanelData(lots, invoice, isLoading);

  let footer: ReactNode | undefined;

  if (invoice?.status == "draft" && invoice?.clientType == "Seller") {
    // It's a draft statement
    footer = <PotentialPayoutsSection draftInvoice={invoice as DraftInvoice} />;
  } else if (invoice?.status !== "draft") {
    // It's a finalized invoice with actual payouts
    footer = <ActualPayoutsSection invoice={invoice as Invoice} />;
  }

  return <Panel {...panelData} footer={footer} />;
}

// The panel itself
interface PanelProps {
  title: string;

  aggregates: {
    label: string;
    value: string | null;
  }[];

  subtotals: {
    label: string;
    value: string | null;
  }[];

  total: string;

  amountPaid?: string;
  amountDue?: string;

  isLoading: boolean;

  footer?: ReactNode;
}

function Panel(props: PanelProps) {
  let {
    title,
    aggregates,
    subtotals,
    total,
    isLoading,
    amountPaid,
    amountDue,
  } = props;

  let hasPaymentInfo = amountPaid != null;

  return (
    <div className="flex flex-shrink-0 w-72 flex-col rounded-md bg-[#0E1513] text-sm font-light text-[#CBCBCB]">
      <div className="py-4">
        <h3 className="mb-6 px-5 text-lg font-medium flex flex-row items-center justify-between">
          {title}
          {isLoading ? <Spinner /> : null}
        </h3>

        <ul className="flex-grow">
          {aggregates.map((a, idx) => (
            <li
              key={`${a.label}`}
              className={classNames(
                "flex-row flex justify-between px-5 py-1.5",
                idx % 2 === 0 ? "bg-white/10" : "",
                isLoading ? "animate-pulse" : ""
              )}
            >
              <span>{a.label}</span>
              <span>{a.value}</span>
            </li>
          ))}
        </ul>

        <ul className="mb-2 px-5 mt-32">
          {subtotals.map((s) => (
            <li
              key={`${s.label}`}
              className={classNames(
                "flex flex-row justify-between border-b border-b-white/20 py-1.5",
                isLoading ? "animate-pulse" : ""
              )}
            >
              <span className="mr-3">{s.label}</span>
              <span>{s.value}</span>
            </li>
          ))}
        </ul>

        <div
          className={classNames(
            "flex flex-row items-center justify-between px-5 py-1.5",
            isLoading ? "animate-pulse" : ""
          )}
        >
          <span className="text-sm font-bold text-[#7A8582]">TOTAL</span>
          <span
            className={classNames(
              "font-bold",
              hasPaymentInfo ? "text-lg " : "text-2xl text-white"
            )}
          >
            {total}
          </span>
        </div>
      </div>

      {props.footer}
    </div>
  );
}

function ActualPayoutsSection(props: { invoice: Invoice }) {
  let { invoice } = props;

  let _payoutValues = Object.values(invoice?.payoutsById || {});
  let _paymentValues = Object.values(invoice?.paymentsInById || {});

  let values = [..._payoutValues, ..._paymentValues];

  if (values.length === 0) {
    return null;
  }

  // Helper function to format date in "DD MMM YYYY" format
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";

    try {
      // Handle both Firestore Timestamp and seconds timestamp
      let date;
      if (typeof timestamp.toDate === "function") {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }

      // Format as "DD MMM YYYY" (e.g., "2 Feb 2024")
      return (
        date.getDate() +
        " " +
        date.toLocaleString("en-US", { month: "short" }) +
        " " +
        date.getFullYear()
      );
    } catch (e) {
      return "Invalid date";
    }
  };

  // Helper function to check if a payment/payout is voided
  const isVoided = (p: SimplePaymentOut | SimplePaymentIn) => {
    // For payments, check the status property
    if ("status" in p) {
      return p.status === "void";
    }

    // For payouts, check if there's a status property even though it's not in the type
    if ("payoutId" in p && (p as any).status === "void") {
      return true;
    }

    return false;
  };

  const totalAmountInCents = invoice.amountPaidInCents;

  return (
    <div className="bg-[#2F794A] text-white rounded-b-md py-4">
      <h3 className="mb-3 px-5 text-base font-medium flex flex-row items-center gap-2">
        <PotentialPayoutsIcon />
        {invoice.clientType === "Seller" ? "Payouts" : "Payments"}
      </h3>

      <ul className="flex flex-col mb-3">
        {values.map((p: SimplePaymentOut | SimplePaymentIn, idx) => {
          let isVoidedPayment = isVoided(p);
          return (
            <li
              key={`${(p as any).payoutId || (p as any).paymentId || idx}`}
              className={`flex flex-row justify-between items-center py-1.5 mx-5 border-b border-white/20 ${
                isVoidedPayment ? "opacity-70" : ""
              }`}
            >
              <span className="font-medium w-1/3 text-white/70">
                {paymentMethodLabel(p.paymentMethod)}
              </span>
              <span className="text-white w-1/3 text-white/70">
                {formatDate(p.paidAt)}
              </span>
              <span className="text-right w-1/3 text-white/70">
                {isVoidedPayment ? (
                  <span className="font-medium text-white/90">VOID</span>
                ) : (
                  formatAsCurrency(null, p.amountInCents, removeTrailingZero)
                )}
              </span>
            </li>
          );
        })}

        <li className="flex flex-row justify-between items-center pt-1.5 mx-5 ">
          {/* <span className="font-medium w-1/3"></span> */}
          <span className="text-white w-2/3 font-medium text-white/90">
            Amount Paid
          </span>
          <span className="text-right w-1/3 font-medium text-white/90">
            {formatAsCurrency(null, totalAmountInCents, removeTrailingZero)}
          </span>
        </li>
      </ul>

      {/* Separator line */}
      {/* <div className="h-px bg-white/20 mx-5 mb-2"></div> */}

      {/* Amount Due */}
      <div className="flex flex-row items-center justify-between px-5 py-1.5">
        <span className="text-sm font-bold text-white/90">AMOUNT DUE</span>
        <span className="text-2xl font-bold text-white">
          {formatAsCurrency(
            invoice.currency,
            invoice.amountDueInCents || 0,
            removeTrailingZero
          )}
        </span>
      </div>
    </div>
  );
}

function paymentMethodLabel(method: PaymentMethod | PayoutMethod) {
  switch (method) {
    case "Credit":
      return "Contra";
    case "BankTransfer":
      return "Bank Tx";
    default:
      return method;
  }
}

function PotentialPayoutsSection(props: { draftInvoice: DraftInvoice }) {
  let { draftInvoice } = props;

  // Safely access potentialPayoutsById only if it exists on the invoice object
  let potentialPayouts = draftInvoice.potentialPayoutsById || {};

  // Get state and functions from the checkout sheet context
  const {
    applyParams,
    isLoading: isDraftLoading,
    isIssuingInvoice,
    extraParams,
  } = useCheckoutSheetContext();

  const currentSettleDebtsFirst =
    draftInvoice?.payoutParams?.settleDebtsFirst ?? false;

  const currentPayoutMethod =
    draftInvoice?.payoutMethod ?? extraParams?.payout?.method;

  let hasPayouts = potentialPayouts && Object.keys(potentialPayouts).length > 0;
  const isBusy = isDraftLoading || isIssuingInvoice;

  const handleSettleDebtsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (applyParams && currentPayoutMethod) {
      applyParams({
        payout: {
          method: currentPayoutMethod,
          settleDebtsFirst: event.target.checked,
        },
      });
    }
  };

  const handlePayoutMethodChange = (method: PayoutMethod) => {
    if (applyParams && !isBusy) {
      applyParams({
        payout: {
          method: method,
          settleDebtsFirst: currentSettleDebtsFirst,
        },
      });
    }
  };

  if (!hasPayouts) {
    return null;
  }

  return (
    <div className="bg-[#2F794A] text-white rounded-b-md py-4">
      <h3 className="mb-3 px-5 text-base font-medium flex flex-row items-center gap-2">
        <PotentialPayoutsIcon />
        Potential Payouts
      </h3>

      <ul className="flex flex-col mb-3">
        {Object.values(potentialPayouts || {})
          .sort((a, b) => {
            // Sort payouts without invoice first (regular payouts)
            if (
              (!a.invoice && b.invoice) ||
              (a.paymentMethod !== "Credit" && b.paymentMethod === "Credit")
            ) {
              return -1;
            }
            if (
              (a.invoice && !b.invoice) ||
              (a.paymentMethod === "Credit" && b.paymentMethod !== "Credit")
            ) {
              return 1;
            }
            return 0;
          })
          .map((payout: PotentialPayout, idx) => {
            let label: string | JSX.Element = payout.paymentMethod;

            // Show a dropdown for payout methods that don't have an invoice
            if (payout.paymentMethod !== "Credit" || !payout.invoice) {
              // This is a regular payout that can have its method changed
              label = (
                <Listbox
                  value={payout.paymentMethod}
                  onChange={handlePayoutMethodChange}
                  disabled={isBusy}
                >
                  <div className="relative">
                    <Listbox.Button className="inline-flex w-full items-center justify-between rounded-md px-2 py-1 text-xs font-medium bg-white/10 hover:bg-white/20 disabled:opacity-50">
                      <span className="block truncate">
                        {paymentMethodLabel(
                          payout.paymentMethod as PaymentMethod
                        )}
                      </span>
                      <ChevronDownIcon
                        className="-mr-1 ml-1 h-3 w-3"
                        aria-hidden="true"
                      />
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute right-0 z-10 mt-1 w-32 origin-top-right rounded-md bg-[#1F553A] shadow-lg ring-1 ring-black/10 focus:outline-none">
                        {PAYOUT_METHODS.map((method) => (
                          <Listbox.Option
                            key={method}
                            value={method}
                            className={({ active, selected }) =>
                              classNames(
                                "cursor-pointer select-none relative py-2 pl-3 pr-9 text-xs rounded-md",
                                active
                                  ? "bg-[#2A6649] text-white"
                                  : "text-gray-100",
                                selected ? "bg-[#2A6649]" : ""
                              )
                            }
                          >
                            {({ selected }) => (
                              <span
                                className={classNames(
                                  "block truncate",
                                  selected
                                    ? "font-medium text-white"
                                    : "font-normal text-white/90"
                                )}
                              >
                                {method}
                              </span>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              );
            } else if (payout.paymentMethod === "Credit" && payout.invoice) {
              label = (
                <span className="flex items-center gap-1">
                  Invoice #{payout.invoice.invoiceNumber}
                  {payout.invoice.fullyPaid ? (
                    <span className="text-xs px-1 py-0.5 bg-green-900/50 rounded-sm select-none">
                      Pays in Full
                    </span>
                  ) : (
                    <span className="text-xs px-1 py-0.5 bg-green-900/50 rounded-sm select-none">
                      Partial Payment
                    </span>
                  )}
                </span>
              );
            }

            return (
              <li
                key={`${payout.id || idx}`}
                className="flex flex-row justify-between items-center px-5 py-1.5 border-b border-white/20 last:border-b-0"
              >
                <span>{label}</span>
                <span>
                  {formatAsCurrency(
                    null,
                    payout.amountInCents,
                    removeTrailingZero
                  )}
                </span>
              </li>
            );
          })}
      </ul>

      {/* --- Settle Debts Checkbox --- */}
      <div className="px-5 py-1 flex items-center justify-end">
        <label
          htmlFor="settle-debts-first"
          className={`text-[10px] font-light text-white/70 mr-1.5 cursor-pointer ${
            isBusy ? "opacity-40" : ""
          }`}
        >
          Settle Debts First
        </label>
        <input
          id="settle-debts-first"
          name="settle-debts-first"
          type="checkbox"
          checked={currentSettleDebtsFirst}
          onChange={handleSettleDebtsChange}
          disabled={isBusy}
          className="h-3 w-3 rounded cursor-pointer border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
        />
      </div>
      {/* --- /Settle Debts Checkbox --- */}
    </div>
  );
}
